<template>
  <template v-if="worker">
    <div class="side-panel">
      <template v-if="!simple">
        <div class="buttons">
          <EditForm
            class="is-primary"
            :worker="worker"
          />
          <button
            type="button"
            v-if="!worker.archived"
            class="button is-danger"
            v-on:click="archiveModal = authStore.canArchive"
            :disabled="!authStore.canArchive"
            :title="
              authStore.canArchive
                ? 'Archive worker'
                : 'You do not have the necessary permission to archive a worker'
            "
          >
            Archive worker
          </button>
        </div>
        <ArchivalModal
          v-if="worker"
          :instance="worker"
          instance-type="worker"
          v-model="archiveModal"
          :update-function="workerStore.updateWorker"
        />
      </template>

      <RecommendedVersionButton
        :worker-id="worker.id"
        :select-function="selectFunction"
      />
    </div>

    <div class="title">
      <span
        v-if="worker.archived"
        class="tag"
        >Archived</span
      >
      <!-- Add a link to the worker details page anytime we are showing this component outside of that page -->
      <WorkerTag
        :worker="worker"
        :with-link="simple || $route.name !== 'worker-manage'"
      />
    </div>
    <div class="subtitle is-5">
      <p>
        Worker <ItemId :item-id="worker.id" />
        <span
          v-if="hasCosts(worker)"
          class="tag is-warning"
          >€</span
        >
      </p>
    </div>

    <Tabs
      :tabs="tabs"
      v-model="tab"
    >
      <template v-slot:details>
        <!-- eslint-disable vue/no-v-html -->
        <p
          v-if="workerDescription"
          class="content"
          v-html="workerDescription"
        ></p>
        <!-- eslint-enable vue/no-v-html -->
        <p v-else><em>No description available</em></p>

        <template v-if="!simple">
          <hr />
          <div
            class="field"
            v-if="!simple"
          >
            <label class="label">Repository</label>
            <div class="control">
              <p
                v-if="worker.repository_url"
                class="content"
              >
                <a
                  :href="worker.repository_url"
                  target="_blank"
                >
                  {{ worker.repository_url }}
                </a>
              </p>
              <p v-else>—</p>
            </div>
          </div>
        </template>
      </template>
      <template v-slot:versions>
        <VersionList
          :worker="worker"
          :select-function="selectFunction"
          :only-select-available="onlySelectAvailable"
        />
      </template>
      <template v-slot:members>
        <ListMembers
          v-if="authStore.hasFeature('enterprise') && !simple"
          content-type="worker"
          :content-id="workerId"
        />
      </template>
      <template
        v-slot:costs
        v-if="hasCosts(worker)"
      >
        <WorkerCosts :worker="worker" />
      </template>
    </Tabs>
  </template>
  <div
    v-else-if="error"
    class="notification is-warning"
  >
    {{ error }}
  </div>
  <div
    v-else
    class="notification is-info"
  >
    Loading…
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import ArchivalModal from '@/components/ArchivalModal.vue'
import ItemId from '@/components/ItemId.vue'
import ListMembers from '@/components/Memberships/ListMembers.vue'
import Tabs from '@/components/Tabs.vue'
import { errorParser } from '@/helpers'
import { markdownToHtml } from '@/helpers/markdown'
import { hasCosts } from '@/helpers/worker'
import { useAuthStore, useWorkerStore } from '@/stores'
import type { UUID } from '@/types'
import type { Worker, WorkerVersion } from '@/types/worker'

import WorkerCosts from './Costs.vue'
import EditForm from './EditForm.vue'
import VersionList from './Versions/List.vue'
import RecommendedVersionButton from './Versions/RecommendedButton.vue'
import WorkerTag from './WorkerRuns/WorkerTag.vue'

const { workerId, selectFunction, onlySelectAvailable, simple } = defineProps<{
  workerId: UUID
  /**
   * A function called when a WorkerVersion is selected.
   * The button to select a worker version is only shown when this function is set.
   */
  selectFunction?: (version: WorkerVersion) => unknown | Promise<unknown>
  /**
   * When a `selectFunction` is defined, only a WorkerVersion with an `available` state will be selectable.
   */
  onlySelectAvailable?: boolean
  /**
   * Show less details, useful for inserting this compoennt into a modal
   */
  simple?: boolean
}>()

const loading = ref(false)
const error = ref<string | null>(null)
const archiveModal = ref(false)
const tab = ref('details')

const authStore = useAuthStore()
const workerStore = useWorkerStore()

const worker = computed<Worker | null>(() => workerStore.workers[workerId] ?? null)

const workerDescription = computed<string | null>(() => {
  return worker.value?.description ? markdownToHtml(worker.value.description) : null
})

const tabs = computed<Record<string, string>>(() => {
  const tabs: Record<string, string> = {
    // In simple mode, the details tab only shows the worker's description
    details: simple ? 'Description' : 'Details',
    versions: 'Versions',
  }
  if (authStore.hasFeature('enterprise') && !simple) tabs.members = 'Members'
  if (worker.value && hasCosts(worker.value)) tabs.costs = 'Costs'
  return tabs
})

watch(
  () => workerId,
  async (newValue: UUID) => {
    if (workerStore.workers[newValue]) return
    loading.value = true
    try {
      await workerStore.getWorker(workerId)
    } catch (err) {
      error.value = errorParser(err)
    } finally {
      loading.value = false
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.side-panel {
  float: right;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-left: 0.5rem;
}

.side-panel .buttons {
  margin-left: auto;
  margin-bottom: 0.25rem;
}
</style>
